import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';
import { company } from '../config/company';
import s from '../styles/legalStyles';

const TermsConditions = () => {
  const { language } = useLanguage();
  const isNL = language === 'nl';

  return (
    <div style={s.page}>
      <SEO
        title={isNL ? 'Algemene Voorwaarden' : 'Terms & Conditions'}
        description={isNL
          ? `Algemene voorwaarden van ${company.brand} (${company.name}, KvK ${company.kvk}).`
          : `Terms and conditions of ${company.brand} (${company.name}, CoC ${company.kvk}).`}
      />
      <div style={s.container}>
        <h1 style={s.title}>{isNL ? 'Algemene Voorwaarden' : 'Terms & Conditions'}</h1>
        <p style={s.lastUpdated}>
          {isNL ? 'Laatst bijgewerkt:' : 'Last updated:'} {company.effectiveDate}
        </p>

        {isNL ? (
          <article style={s.article}>
            <h2 style={s.h2}>1. Definities</h2>
            <p>
              In deze voorwaarden wordt verstaan onder:
            </p>
            <ul style={s.list}>
              <li><strong>{company.brand}:</strong> de handelsnaam van {company.name}, ingeschreven bij de KvK onder nummer {company.kvk}, gevestigd te {company.postalCity}.</li>
              <li><strong>Website:</strong> {company.website}</li>
              <li><strong>Bezoeker:</strong> iedere natuurlijke persoon of rechtspersoon die de website bezoekt.</li>
              <li><strong>Partner:</strong> de externe makelaar Nelemans waar Bezoekers naar worden doorverwezen.</li>
            </ul>

            <h2 style={s.h2}>2. Aard van de dienst</h2>
            <p>
              {company.brand} is een informatie- en selectieplatform dat een curated overzicht biedt van Spaans
              vastgoed dat in de aanbieding is bij onze Partner Nelemans. Wij zijn <strong>geen makelaar</strong>,
              treden niet op als bemiddelaar in juridische zin, en hebben geen contractuele relatie met aanbieders of
              kopers van vastgoed.
            </p>
            <p>
              Bij interesse in een woning wordt u doorverwezen naar de website van de Partner. Vanaf dat moment komt
              elke overeenkomst tussen u en de Partner of de uiteindelijke verkoper tot stand, zonder enige tussenkomst
              of aansprakelijkheid van {company.brand}.
            </p>

            <h2 style={s.h2}>3. Aanbod en informatie</h2>
            <p>
              Het op de website getoonde vastgoedaanbod is afkomstig van een live data-feed van Nelemans en kan op elk
              moment wijzigen. Wij streven naar correcte en actuele weergave, maar:
            </p>
            <ul style={s.list}>
              <li>Wij staan niet in voor de juistheid, volledigheid of actualiteit van de getoonde informatie.</li>
              <li>Prijzen, beschikbaarheid en specificaties kunnen afwijken van de werkelijkheid.</li>
              <li>Verifieer alle informatie bij de Partner voordat u een aankoopbeslissing neemt.</li>
            </ul>

            <h2 style={s.h2}>4. Aansprakelijkheid</h2>
            <p>
              {company.brand} is niet aansprakelijk voor enige directe of indirecte schade die voortvloeit uit:
            </p>
            <ul style={s.list}>
              <li>Het gebruik van of vertrouwen op informatie op deze website.</li>
              <li>Transacties tussen Bezoeker en Partner of derden.</li>
              <li>Storingen, fouten of onderbrekingen in de werking van de website.</li>
              <li>Inhoud op externe websites waarnaar wordt gelinkt.</li>
            </ul>
            <p>
              Onze totale aansprakelijkheid is in alle gevallen beperkt tot het bedrag dat onze
              aansprakelijkheidsverzekering in het betreffende geval daadwerkelijk uitkeert.
            </p>

            <h2 style={s.h2}>5. Intellectueel eigendom</h2>
            <p>
              Alle inhoud op de website (teksten, afbeeldingen, logo's, broncode, ontwerp) is eigendom van
              {' '}{company.brand} of haar licentiegevers en wordt beschermd door auteursrecht. Reproductie of
              hergebruik is uitsluitend toegestaan na voorafgaande schriftelijke toestemming.
            </p>

            <h2 style={s.h2}>6. Privacy en cookies</h2>
            <p>
              Op het gebruik van de website is ons{' '}
              <Link style={s.link} to="/privacy">privacybeleid</Link> en{' '}
              <Link style={s.link} to="/cookies">cookiebeleid</Link> van toepassing.
            </p>

            <h2 style={s.h2}>7. Toepasselijk recht en geschillen</h2>
            <p>
              Op deze voorwaarden en het gebruik van de website is uitsluitend Nederlands recht van toepassing.
              Geschillen worden voorgelegd aan de bevoegde rechter in het arrondissement Overijssel, locatie Zwolle,
              tenzij dwingend recht anders bepaalt.
            </p>

            <h2 style={s.h2}>8. Wijzigingen</h2>
            <p>
              {company.brand} behoudt zich het recht voor deze algemene voorwaarden op elk moment te wijzigen. De
              meest actuele versie is altijd raadpleegbaar op deze pagina.
            </p>

            <h2 style={s.h2}>9. Contact</h2>
            <p>
              {company.name}<br />
              {company.street}<br />
              {company.postalCity}<br />
              KvK: {company.kvk}<br />
              E-mail: <a style={s.link} href={`mailto:${company.email}`}>{company.email}</a>
            </p>
          </article>
        ) : (
          <article style={s.article}>
            <h2 style={s.h2}>1. Definitions</h2>
            <p>In these terms the following definitions apply:</p>
            <ul style={s.list}>
              <li><strong>{company.brand}:</strong> the trade name of {company.name}, registered with the Dutch Chamber of Commerce (KvK) under number {company.kvk}, established in {company.postalCity}.</li>
              <li><strong>Website:</strong> {company.website}</li>
              <li><strong>Visitor:</strong> any natural or legal person who visits the website.</li>
              <li><strong>Partner:</strong> the external real estate agent Nelemans to whom Visitors are referred.</li>
            </ul>

            <h2 style={s.h2}>2. Nature of the service</h2>
            <p>
              {company.brand} is an information and curation platform offering a curated overview of Spanish real estate
              listed by our Partner Nelemans. We are <strong>not a real estate agent</strong>, we do not act as a broker
              in any legal sense, and we have no contractual relationship with property sellers or buyers.
            </p>
            <p>
              When interested in a property, you will be referred to the Partner's website. From that moment any
              agreement is between you and the Partner or final seller, without any involvement or liability of
              {' '}{company.brand}.
            </p>

            <h2 style={s.h2}>3. Listings and information</h2>
            <p>
              The property listings displayed on the website come from a live data feed of Nelemans and may change at
              any time. We strive for accurate and up-to-date display, but:
            </p>
            <ul style={s.list}>
              <li>We do not guarantee the accuracy, completeness or timeliness of displayed information.</li>
              <li>Prices, availability and specifications may differ from reality.</li>
              <li>Verify all information with the Partner before making a purchase decision.</li>
            </ul>

            <h2 style={s.h2}>4. Liability</h2>
            <p>{company.brand} is not liable for any direct or indirect damages arising from:</p>
            <ul style={s.list}>
              <li>The use of or reliance on information on this website.</li>
              <li>Transactions between Visitor and Partner or third parties.</li>
              <li>Disruptions, errors or interruptions in the operation of the website.</li>
              <li>Content on external websites that are linked to.</li>
            </ul>
            <p>
              Our total liability is in all cases limited to the amount actually paid out by our liability insurance in
              that specific case.
            </p>

            <h2 style={s.h2}>5. Intellectual property</h2>
            <p>
              All content on the website (text, images, logos, source code, design) is owned by {company.brand} or its
              licensors and protected by copyright. Reproduction or reuse is only permitted with prior written consent.
            </p>

            <h2 style={s.h2}>6. Privacy and cookies</h2>
            <p>
              Use of the website is subject to our <Link style={s.link} to="/privacy">privacy policy</Link> and{' '}
              <Link style={s.link} to="/cookies">cookies policy</Link>.
            </p>

            <h2 style={s.h2}>7. Applicable law and disputes</h2>
            <p>
              These terms and the use of the website are governed exclusively by Dutch law. Disputes will be submitted
              to the competent court in the Overijssel district, Zwolle location, unless mandatory law provides
              otherwise.
            </p>

            <h2 style={s.h2}>8. Changes</h2>
            <p>
              {company.brand} reserves the right to change these terms and conditions at any time. The most current
              version is always available on this page.
            </p>

            <h2 style={s.h2}>9. Contact</h2>
            <p>
              {company.name}<br />
              {company.street}<br />
              {company.postalCity}<br />
              KvK: {company.kvk}<br />
              Email: <a style={s.link} href={`mailto:${company.email}`}>{company.email}</a>
            </p>
          </article>
        )}
      </div>
    </div>
  );
};

export default TermsConditions;
